import './index.css';
import { useRef } from 'react';
import { eachDayOfInterval, isWithinInterval, min, max, isBefore, isAfter } from 'date-fns';
import { useDisabled } from '@hh.ru/magritte-common-use-disabled';

const sortedInterval = (a, b) => {
    return isBefore(a.date, b.date) ? [a, b] : [b, a];
};
/**
 * Возвращает индекс selectedDate в упорядоченном массиве dates или -1 или dates.length, если selectedDate лежит за границами dates
 */
const findSelectedIndex = (dates, selectedDate, isEqual) => {
    if (isBefore(selectedDate, dates[0])) {
        // выбрана дата раньше первой даты dates
        return -1;
    }
    else if (isAfter(selectedDate, dates[dates.length - 1])) {
        // выбрана дата позже последней даты dates
        return dates.length;
    }
    // выбрана дата в границах dates
    const _selectionStartIndex = dates.findIndex((date) => isEqual(date, selectedDate));
    return _selectionStartIndex !== -1 ? _selectionStartIndex : null;
};
/**
 * Вычисляет упорядоченный интервал, который нужно нарисовать, учитывая:
 *  - выбранные даты
 *  - дату под ховером
 *  - перетаскивание даты
 */
const makeInterval = ({ dates, selectionStart, selectionEnd, hoveredDate, dragState, isEqual }) => {
    // обе даты выбраны
    if (!!selectionStart && !!selectionEnd) {
        if (dragState?.endpoint === 'start') {
            // обе даты выбраны, дата начала перетаскивается
            return {
                interval: sortedInterval(dragState.dropTargetDate
                    ? { date: dragState.dropTargetDate, isSelected: false, isDropTarget: true }
                    : { date: selectionStart, isSelected: false }, { date: selectionEnd, isSelected: true }),
                selectedDate: selectionEnd,
                selectedIndex: findSelectedIndex(dates, selectionEnd, isEqual),
            };
        }
        else if (dragState?.endpoint === 'end') {
            // обе даты выбраны, дата конца перетаскивается
            return {
                interval: sortedInterval({ date: selectionStart, isSelected: true }, dragState.dropTargetDate
                    ? { date: dragState.dropTargetDate, isSelected: false, isDropTarget: true }
                    : { date: selectionEnd, isSelected: false }),
                selectedDate: selectionStart,
                selectedIndex: findSelectedIndex(dates, selectionStart, isEqual),
            };
        }
        // обе даты выбраны, ничего не перетаскивается
        return {
            interval: sortedInterval({ date: selectionStart, isSelected: true }, { date: selectionEnd, isSelected: true }),
            selectedDate: null,
            selectedIndex: null,
        };
    }
    // только дата начала выбрана
    if (!!selectionStart && !selectionEnd) {
        if (hoveredDate) {
            // дата начала выбрана, дата конца выбирается ховером в этом календаре
            return {
                interval: sortedInterval({ date: selectionStart, isSelected: true }, { date: hoveredDate, isSelected: false }),
                selectedDate: selectionStart,
                selectedIndex: findSelectedIndex(dates, selectionStart, isEqual),
            };
        }
        return {
            interval: [{ date: selectionStart, isSelected: true }],
            selectedDate: selectionStart,
            selectedIndex: findSelectedIndex(dates, selectionStart, isEqual),
        };
    }
    // только дата конца выбрана
    if (!selectionStart && !!selectionEnd) {
        if (hoveredDate) {
            // дата конца выбрана, дата конца выбирается ховером в этом календаре
            return {
                interval: sortedInterval({ date: hoveredDate, isSelected: true }, { date: selectionEnd, isSelected: true }),
                selectedDate: selectionEnd,
                selectedIndex: findSelectedIndex(dates, selectionEnd, isEqual),
            };
        }
        return {
            interval: [{ date: selectionEnd, isSelected: true }],
            selectedDate: selectionEnd,
            selectedIndex: findSelectedIndex(dates, selectionEnd, isEqual),
        };
    }
    return { interval: null, selectedDate: null, selectedIndex: null };
};
const makeDragSourceHandlers = ({ date, isIntervalStart, isIntervalEnd, onDateDrag, dragImageRef, dragSourceRef, }) => {
    return {
        onDragStart: (event) => {
            event.dataTransfer.effectAllowed = 'move';
            const target = event.target;
            dragSourceRef.current = target;
            const metrics = target.getBoundingClientRect();
            const dragImage = target.cloneNode(true);
            dragImageRef.current = dragImage;
            dragImage.style.position = 'absolute';
            dragImage.style.left = '-9999px';
            dragImage.style.top = '-9999px';
            dragImage.style.cursor = 'grabbing';
            target.parentNode?.appendChild(dragImage);
            event.dataTransfer.setDragImage(dragImage, event.clientX - metrics.left, event.clientY - metrics.top);
            if (isIntervalStart) {
                onDateDrag?.({ endpoint: 'start', dragSourceDate: date, dropTargetDate: null });
            }
            else if (isIntervalEnd) {
                onDateDrag?.({ endpoint: 'end', dragSourceDate: date, dropTargetDate: null });
            }
        },
    };
};
const makeDropTargetHandlers = ({ date, selectedDate, dragState, onStartSelect, onEndSelect, onDateDrag, dragImageRef, dragSourceRef, onDragLeaveRef, }) => {
    return {
        onDragEnter: () => {
            if (onDragLeaveRef.current !== null) {
                clearTimeout(onDragLeaveRef.current);
                onDragLeaveRef.current = null;
            }
            if (dragState) {
                onDateDrag?.({ ...dragState, dropTargetDate: date });
            }
        },
        onDragOver: (event) => {
            event.preventDefault();
        },
        onDragLeave: () => {
            if (onDragLeaveRef.current !== null) {
                clearTimeout(onDragLeaveRef.current);
            }
            onDragLeaveRef.current = setTimeout(() => {
                if (dragState) {
                    onDateDrag?.({ ...dragState, dropTargetDate: null });
                }
            }, 200);
        },
        onDragEnd: () => {
            if (dragImageRef.current !== null) {
                dragImageRef.current.parentNode?.removeChild(dragImageRef.current);
                dragImageRef.current = null;
            }
            if (dragSourceRef.current !== null) {
                dragSourceRef.current.style.pointerEvents = '';
            }
            if (dragState?.dropTargetDate) {
                onStartSelect?.(min([dragState.dropTargetDate, selectedDate]));
                onEndSelect?.(max([dragState.dropTargetDate, selectedDate]));
            }
            onDateDrag?.(null);
        },
        onDrop: () => {
            // после завершения драг-н-дропа хром убирает состояние :active с перетаскиваемого элемента с задержкой,
            // из-за чего он мерцает, pointer-events:none решает эту проблему
            if (dragSourceRef.current !== null) {
                dragSourceRef.current.style.pointerEvents = 'none';
            }
        },
    };
};
const useIntervalPicker = ({ dates, disabledDatesFunc, selectionStart, selectionEnd, hoveredDate, dragState, onStartSelect, onEndSelect, onDateHover, onDateDrag, isEqual, }) => {
    const forceDisabled = useDisabled();
    const dragImageRef = useRef(null);
    const dragSourceRef = useRef(null);
    const onMouseLeaveRef = useRef(null);
    const onDragLeaveRef = useRef(null);
    const isTouchEnabledRef = useRef(false);
    const { interval, selectedDate, selectedIndex } = makeInterval({
        dates,
        selectionStart,
        selectionEnd,
        hoveredDate,
        dragState,
        isEqual,
    });
    const disabledProps = forceDisabled || !disabledDatesFunc ? null : dates.map(disabledDatesFunc);
    if (disabledProps !== null) {
        // если выбрана только одна дата, нужно сделать недоступными все даты,
        // которые отделены от выбранной даты другой недоступной датой
        if (selectedIndex !== null && selectedDate !== null) {
            if (selectedIndex === -1) {
                // выбрана дата начала в предыдущем месяце
                disabledProps[selectedIndex] = eachDayOfInterval({ start: selectedDate, end: dates[0] }).some(disabledDatesFunc);
            }
            else if (selectedIndex === dates.length) {
                // выбрана дата начала в следующем месяце
                disabledProps[selectedIndex] = eachDayOfInterval({
                    start: dates[dates.length - 1],
                    end: selectedDate,
                }).some(disabledDatesFunc);
            }
            for (let i = Math.max(selectedIndex + 1, 0); i <= dates.length - 1; i += 1) {
                disabledProps[i] ||= disabledProps[i - 1];
            }
            for (let i = Math.min(selectedIndex - 1, dates.length - 1); i >= 0; i -= 1) {
                disabledProps[i] ||= disabledProps[i + 1];
            }
        }
    }
    return dates.map((date, index) => {
        let isIntervalStart = false;
        let isIntervalEnd = false;
        let isSelected = false;
        const isDraggable = !!selectionStart && !!selectionEnd;
        const isDragSource = !!dragState && isEqual(date, dragState.dragSourceDate);
        let isDropTarget = false;
        let isWithinInterval$1 = false;
        if (interval !== null) {
            isIntervalStart = isEqual(date, interval[0].date);
            if (isIntervalStart) {
                isSelected = interval[0].isSelected;
                isDropTarget = interval[0].isDropTarget ?? isDragSource;
            }
            if (interval[1]) {
                isIntervalEnd = !!interval[1]?.date && isEqual(date, interval[1].date);
                if (isIntervalEnd) {
                    isSelected = interval[1].isSelected;
                    isDropTarget = interval[1].isDropTarget ?? isDragSource;
                }
                isWithinInterval$1 = isWithinInterval(date, { start: interval[0].date, end: interval[1].date });
            }
        }
        else {
            isSelected = index === selectedIndex;
        }
        const onClick = () => {
            if (interval !== null) {
                // перед кликом были выбраны обе даты — сбрасываем интервал, кликнутая дата становится датой начала
                if (interval[0].isSelected && interval[1]?.isSelected) {
                    onStartSelect?.(date);
                    onEndSelect?.(null);
                    if (!isTouchEnabledRef.current) {
                        onDateHover?.(null);
                    }
                }
                // перед кликом была выбрана только дата начала — кликнутая дата становится датой конца
                if (interval[0].isSelected && !interval[1]?.isSelected) {
                    onStartSelect?.(min([interval[0].date, date]));
                    onEndSelect?.(max([interval[0].date, date]));
                    if (!isTouchEnabledRef.current) {
                        onDateHover?.(null);
                    }
                }
                // перед кликом была выбрана только дата конца — кликнутая дата становится датой начала
                if (!interval[0].isSelected && interval[1]?.isSelected) {
                    onStartSelect?.(min([date, interval[1].date]));
                    onEndSelect?.(max([date, interval[1].date]));
                    if (!isTouchEnabledRef.current) {
                        onDateHover?.(null);
                    }
                }
            }
            else {
                // перед кликом не была выбрана ни одна дата — кликнутая дата становится датой начала
                onStartSelect?.(date);
                if (!isTouchEnabledRef.current) {
                    onDateHover?.(null);
                }
            }
        };
        const onFocus = () => {
            if (!isTouchEnabledRef.current) {
                onDateHover?.(date);
            }
        };
        const onMouseEnter = () => {
            if (!isTouchEnabledRef.current) {
                if (onMouseLeaveRef.current !== null) {
                    clearTimeout(onMouseLeaveRef.current);
                    onMouseLeaveRef.current = null;
                }
                onDateHover?.(date);
            }
        };
        const onMouseLeave = () => {
            if (!isTouchEnabledRef.current) {
                if (onMouseLeaveRef.current !== null) {
                    clearTimeout(onMouseLeaveRef.current);
                }
                if (hoveredDate) {
                    onMouseLeaveRef.current = setTimeout(() => {
                        onDateHover?.(null);
                    }, 200);
                }
            }
        };
        const onTouchStart = () => {
            isTouchEnabledRef.current = true;
        };
        return {
            isDisabled: forceDisabled || (disabledProps?.[index] ?? false),
            isIntervalStart,
            isIntervalEnd,
            isSelected,
            isDraggable: isDraggable && isSelected,
            isDragSource,
            isDropTarget,
            isWithinInterval: isWithinInterval$1,
            mouseHandlers: { onClick, onFocus, onMouseEnter, onMouseLeave, onTouchStart },
            dragSourceHandlers: isDraggable && isSelected
                ? makeDragSourceHandlers({
                    date,
                    isIntervalStart,
                    isIntervalEnd,
                    onDateDrag,
                    dragImageRef,
                    dragSourceRef,
                })
                : undefined,
            dropTargetHandlers: dragState
                ? makeDropTargetHandlers({
                    date,
                    selectedDate: selectedDate,
                    dragState,
                    onStartSelect,
                    onEndSelect,
                    onDateDrag,
                    dragImageRef,
                    dragSourceRef,
                    onDragLeaveRef,
                })
                : undefined,
        };
    });
};

export { useIntervalPicker };
//# sourceMappingURL=useIntervalPicker.js.map
